import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";

// Removed static placeholder images — use admin-uploaded banners only
const defaultDesktopBanners = [];
const defaultMobileBanners = [];
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || ((window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1") ? "https://server.havbit.in/api" : "https://server.havbit.in/api");

export default function BannerCarousel () {
  const navigate = useNavigate();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [direction, setDirection] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [banners, setBanners] = useState([]);
  const [title, setTitle] = useState("");

  useEffect(() => {
    const checkRes = () => setIsMobile(window.innerWidth < 768);
    checkRes();
    window.addEventListener("resize", checkRes);
    return () => window.removeEventListener("resize", checkRes);
  }, []);

  useEffect(() => {
    const fetchHomepageBanner = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/site-settings/homepage`);
        if (!res.ok) return;
        const data = await res.json();
        if (data?.success && data.data) {
          const bannersData = data.data.banner || [];
          const newTitle = data.data.title || "";
          setTitle(newTitle);

          if (Array.isArray(bannersData) && bannersData.length) {
            setBanners(bannersData.map((banner) => (
              typeof banner === "string" ? { url: banner } : banner
            )).filter((banner) => banner.url));
          } else if (bannersData && bannersData.url) {
            setBanners([bannersData]);
          } else {
            // No banners returned from API — keep empty list so only admin-uploaded images are used
            setBanners([]);
          }
        }
      } catch (error) {
        console.error("Failed to load homepage banner:", error);
      }
    };

    fetchHomepageBanner();
  }, []);

  const usedBanners = isMobile ? (banners.length ? banners : defaultMobileBanners) : (banners.length ? banners : defaultDesktopBanners);
  const currentBanner = usedBanners[currentIndex];
  const productPath = currentBanner?.productId
    ? `/product/${currentBanner.productId}`
    : currentBanner?.productSlug
      ? `/product/${currentBanner.productSlug}`
      : null;
  const Slide = productPath ? motion.button : motion.div;

  const nextSlide = useCallback(() => {
    if (!usedBanners.length) return;
    setDirection(1);
    setCurrentIndex((prev) => (prev + 1) % usedBanners.length);
  }, [usedBanners.length]);

  const prevSlide = useCallback(() => {
    if (!usedBanners.length) return;
    setDirection(-1);
    setCurrentIndex((prev) => (prev - 1 + usedBanners.length) % usedBanners.length);
  }, [usedBanners.length]);

  useEffect(() => {
    if (currentIndex >= usedBanners.length) {
      setCurrentIndex(0);
    }
  }, [currentIndex, usedBanners.length]);

  // Auto-slide 3 seconds (Fast)
  useEffect(() => {
    if (isPaused || usedBanners.length === 0) return;
    const interval = setInterval(nextSlide, 3000);
    return () => clearInterval(interval);
  }, [isPaused, nextSlide, usedBanners.length]);

  // Smooth Fast Variants (No Bounce)
  const variants = {
    enter: (direction) => ({
      x: direction > 0 ? "100%" : "-100%",
      opacity: 0,
    }),
    center: {
      x: 0,
      opacity: 1,
      transition: {
        x: { type: "tween", duration: 0.3, ease: "easeOut" },
        opacity: { duration: 0.2 }
      }
    },
    exit: (direction) => ({
      x: direction < 0 ? "100%" : "-100%",
      opacity: 0,
      transition: {
        x: { type: "tween", duration: 0.3, ease: "easeOut" },
        opacity: { duration: 0.2 }
      }
    })
  };

  return (
    /* Aapka original layout h-32 (mobile) aur md:h-64 (desktop) */
    <div
      className="relative w-full aspect-[16/7] rounded-xl md:rounded-2xl overflow-hidden shadow-lg cursor-grab active:cursor-grabbing bg-gray-100"
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
      onTouchStart={() => setIsPaused(true)}
      onTouchEnd={() => setIsPaused(false)}
    >
      <AnimatePresence initial={false} custom={direction} mode="popLayout">
        {usedBanners.length ? (
          <Slide
            {...(productPath ? { type: "button" } : {})}
            key={currentIndex}
            custom={direction}
            variants={variants}
            initial="enter"
            animate="center"
            exit="exit"
            drag="x"
            dragConstraints={{ left: 0, right: 0 }}
            dragElastic={0.05}  
            onDragEnd={(e, { offset }) => {
              if (offset.x < -50) nextSlide();
              else if (offset.x > 50) prevSlide();
            }}
            {...(productPath ? {
              "aria-label": "View featured product",
              onTap: () => navigate(productPath),
            } : {})}
            className={`absolute w-full h-full border-0 p-0 ${productPath ? "cursor-pointer" : "cursor-grab active:cursor-grabbing"}`}
          >
            <img
              src={usedBanners[currentIndex]?.url || usedBanners[currentIndex]}
              alt="Banner"
              loading="eager"
              fetchPriority="high"
              className="w-full h-full object-cover object-center select-none bg-[rgb(235,236,230)]"
              draggable="false"
            />
          </Slide>
        ) : (
          <div className="w-full h-full flex items-center justify-center text-sm text-slate-500">
            No banners configured. Upload from admin panel.
          </div>
        )}
      </AnimatePresence>
    </div>
  );
} 